import { useCallback, useRef } from 'react';

import { useMainPanelSizes, usePanelCollapse } from '../../store/layoutStore';
import { useCollapsiblePane } from './useCollapsiblePane';
import { COLLAPSED_SIDE_PANEL_WIDTH_PX } from './collapsedPanelWidth';

const DOMAIN_TREE_MIN_WIDTH_PX = 320;
const DOMAIN_TREE_MAX_WIDTH_PX = 700;
const DOMAIN_TREE_SNAP_THRESHOLD_PX = 200;
const DOMAIN_TREE_CONTENT_MIN_WIDTH_PX = 400;
const DEFAULT_DOMAIN_TREE_SIZE_PERCENT = 20;

export function useDomainTreeMainLayout() {
  const { isDomainTreeCollapsed, setDomainTreeCollapsed } = usePanelCollapse();
  const { mainPanelSizes, setMainPanelSizes } = useMainPanelSizes();
  const mainPanelSizesRef = useRef(mainPanelSizes);
  mainPanelSizesRef.current = mainPanelSizes;

  const handleResizeEnd = useCallback(
    (domainTreeSize: number, mainSize: number, shouldCollapse: boolean) => {
      setMainPanelSizes(
        mainPanelSizesRef.current.map((panelEntry) =>
          panelEntry.id === 'domain-tree'
            ? {
                ...panelEntry,
                size: shouldCollapse ? panelEntry.size : domainTreeSize,
              }
            : panelEntry.id === 'main'
              ? { ...panelEntry, size: mainSize }
              : panelEntry,
        ),
      );
    },
    [setMainPanelSizes],
  );

  return useCollapsiblePane({
    paneId: 'domain-tree',
    contentId: 'main',
    panePosition: 'start',
    minWidthPx: DOMAIN_TREE_MIN_WIDTH_PX,
    maxWidthPx: DOMAIN_TREE_MAX_WIDTH_PX,
    minContentWidthPx: DOMAIN_TREE_CONTENT_MIN_WIDTH_PX,
    collapsedWidthPx: COLLAPSED_SIDE_PANEL_WIDTH_PX,
    snapThresholdPx: DOMAIN_TREE_SNAP_THRESHOLD_PX,
    persistedSize:
      mainPanelSizes.find((panelEntry) => panelEntry.id === 'domain-tree')
        ?.size ?? DEFAULT_DOMAIN_TREE_SIZE_PERCENT,
    isCollapsed: isDomainTreeCollapsed,
    onCollapseChange: setDomainTreeCollapsed,
    onResizeEnd: handleResizeEnd,
  });
}
